// Modal component for creating a new short link with UTM builder, device targeting, tags
import React, { useState, useEffect } from "react";
import { FiX, FiCheck, FiChevronDown, FiChevronUp } from "react-icons/fi";
import { api } from "../utils/api";
import { useToast } from "./ToastProvider";
import {
  localInputToUTC,
  formatDateTimeWithTz,
  getTodayDateString,
  getUserTimezone,
  getTimezoneAbbreviation,
} from "../utils/timezone";
import "../styles/Modal.css";

const BASE_DOMAIN = (process.env.REACT_APP_BASE_URL || "https://linksquire.com").replace(/\/+$/, "");

// Validate URL format (http/https)
function isValidUrl(url) {
  if (!url) return true; // empty is valid (not required)
  try {
    const u = new URL(url);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

export default function CreateLinkModal({ onClose, onCreated, prefillCampaignId = "" }) {
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [campaignId, setCampaignId] = useState(prefillCampaignId);
  const [campaigns, setCampaigns] = useState([]);
  const [customCode, setCustomCode] = useState("");
  const [password, setPassword] = useState("");
  const [tags, setTags] = useState("");
  const [mobileUrl, setMobileUrl] = useState("");
  const [redirectTime, setRedirectTime] = useState(10);
  const [redirectType, setRedirectType] = useState("302");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const { addToast } = useToast();

  // Lock body scroll while modal is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    api.getCampaigns().then(res => setCampaigns(res.campaigns || [])).catch(console.error);
    return () => { document.body.style.overflow = ""; };
  }, []);

  // Expiry split date/time
  const [expiryDate, setExpiryDate] = useState("");
  const [expiryTime, setExpiryTime] = useState("12:00");

  // Scheduling state
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("00:00");

  // UTM Builder state
  const [showUtm, setShowUtm] = useState(false);
  const [utm, setUtm] = useState({ source: "", medium: "", campaign: "", term: "", content: "" });

  // Base domain options
  const defaultDomain = (process.env.REACT_APP_BASE_URL || "https://sqir.link").replace(/^https?:\/\//, '').replace(/\/+$/, "");
  const [availableDomains, setAvailableDomains] = useState([defaultDomain]);
  const [selectedDomain, setSelectedDomain] = useState(defaultDomain);

  // Advanced options toggle
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Custom OG tags state
  const [useCustomOg, setUseCustomOg] = useState(false);
  const [ogTitle, setOgTitle] = useState("");
  const [ogDescription, setOgDescription] = useState("");
  const [ogImage, setOgImage] = useState("");

  // Expiration fallback state
  const [showFallback, setShowFallback] = useState(false);
  const [fallbackMode, setFallbackMode] = useState("");
  const [fallbackUrl, setFallbackUrl] = useState("");
  const [fallbackMessage, setFallbackMessage] = useState("");
  const [fallbackCtaText, setFallbackCtaText] = useState("");
  const [fallbackCtaUrl, setFallbackCtaUrl] = useState("");
  const [fallbackAutoRedirect, setFallbackAutoRedirect] = useState(false);
  const [fallbackDelay, setFallbackDelay] = useState(5);
  const [customBrandIcon, setCustomBrandIcon] = useState("");
  const [customBrandName, setCustomBrandName] = useState("");

  // Validation for fallback fields
  const fallbackUrlValid = fallbackMode !== "redirect" || isValidUrl(fallbackUrl);
  const fallbackCtaUrlValid = fallbackMode !== "page" || isValidUrl(fallbackCtaUrl);
  const fallbackDelayValid = fallbackDelay >= 1 && fallbackDelay <= 60;
  const fallbackFieldsValid = fallbackUrlValid && fallbackCtaUrlValid && fallbackDelayValid;

  // Toggle warning dialog state
  const [showToggleWarning, setShowToggleWarning] = useState(null); // 'instant' or 'custom_og' or null

  const handleToggleInstantRedirect = (checked) => {
    if (checked && useCustomOg) {
      setShowToggleWarning('instant');
    } else {
      setRedirectTime(checked ? 0 : 10);
    }
  };

  const handleToggleCustomOg = (checked) => {
    if (checked && redirectTime === 0) {
      setShowToggleWarning('custom_og');
    } else {
      setUseCustomOg(checked);
    }
  };

  const confirmToggle = () => {
    if (showToggleWarning === 'instant') {
      setUseCustomOg(false);
      setOgTitle("");
      setOgDescription("");
      setOgImage("");
      setRedirectTime(0);
    } else if (showToggleWarning === 'custom_og') {
      setRedirectTime(10);
      setUseCustomOg(true);
    }
    setShowToggleWarning(null);
  };

  // Fetch domains on mount
  useEffect(() => {
    async function fetchDomains() {
      try {
        const data = await api.getDomains();
        // Since apiRequest returns response.json(), data.domains might not exist if the endpoint 
        // returned an array directly, or it might be data.domains. Let's check both.
        const domainsList = data.domains || data || [];
        if (Array.isArray(domainsList)) {
          const verifiedDomains = domainsList
            .filter(d => d.status === 'active')
            .map(d => d.domain);
          if (verifiedDomains.length > 0) {
            setAvailableDomains([defaultDomain, ...verifiedDomains]);
          }
        }
      } catch (err) {
        console.error("Failed to load domains", err);
      }
    }
    fetchDomains();
  }, [defaultDomain]);

  // OG preview
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    if (!url || url.length < 10) { setPreview(null); return; }
    const t = setTimeout(() => {
      api.getPreview(url).then(setPreview).catch(() => {});
    }, 800);
    return () => clearTimeout(t);
  }, [url]);

  const buildUrlWithUtm = () => {
    if (!url) return "";
    try {
      const u = new URL(url);
      if (utm.source) u.searchParams.set("utm_source", utm.source);
      if (utm.medium) u.searchParams.set("utm_medium", utm.medium);
      if (utm.campaign) u.searchParams.set("utm_campaign", utm.campaign);
      if (utm.term) u.searchParams.set("utm_term", utm.term);
      if (utm.content) u.searchParams.set("utm_content", utm.content);
      return u.toString();
    } catch {
      return url;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const finalUrl = buildUrlWithUtm();
      const body = { original_url: finalUrl };
      if (selectedDomain !== defaultDomain) {
        body.domain = selectedDomain;
      }
      if (title.trim()) body.title = title.trim();
      if (customCode.trim()) body.custom_code = customCode.trim();
      if (campaignId) body.campaign_id = campaignId;
      if (expiryDate) {
        // Convert local date/time to UTC for storage
        body.expires_at = localInputToUTC(expiryDate, expiryTime || "12:00");
        body.timezone = getUserTimezone();
      }
      if (scheduleDate) {
        // Convert local date/time to UTC for storage
        body.scheduled_at = localInputToUTC(scheduleDate, scheduleTime || "00:00");
        body.timezone = getUserTimezone();
      }
      if (password.trim()) body.password = password.trim();
      if (tags.trim()) body.tags = tags.trim();
      if (mobileUrl.trim()) body.mobile_url = mobileUrl.trim();
      body.redirect_time = redirectTime;
      if (redirectType !== "302") body.redirect_type = redirectType;

      if (useCustomOg) {
        if (ogTitle.trim()) body.og_title = ogTitle.trim();
        if (ogDescription.trim()) body.og_description = ogDescription.trim();
        if (ogImage.trim()) body.og_image = ogImage.trim();
      }

      // Fallback fields
      if (fallbackMode) {
        body.fallback_mode = fallbackMode;
        if (fallbackMode === "redirect") {
          body.fallback_url = fallbackUrl;
        } else if (fallbackMode === "page") {
          body.fallback_message = fallbackMessage;
          body.fallback_cta_text = fallbackCtaText;
          body.fallback_cta_url = fallbackCtaUrl;
          body.fallback_auto_redirect = fallbackAutoRedirect;
          body.fallback_delay = fallbackDelay;
        }
      }
      // Custom branding fields (only for page mode)
      if (fallbackMode === "page") {
        body.custom_brand_icon = customBrandIcon.trim();
        body.custom_brand_name = customBrandName.trim();
      }

      const data = await api.createLink(body);
      setResult(data);
      addToast("Link created successfully!", "success");
      if (onCreated) onCreated(data);
    } catch (err) {
      setError(err.message);
      addToast("Failed to create link", "error");
    } finally {
      setLoading(false);
    }
  };

  const [copied, setCopied] = useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(result.short_url);
    setCopied(true);
    addToast("Link copied to clipboard!", "success");
    setTimeout(() => setCopied(false), 1500);
  };

  const hasUtm = Object.values(utm).some(Boolean);
  const todayStr = getTodayDateString();
  const tzAbbrev = getTimezoneAbbreviation();

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{result ? "Link Created!" : "Create Short Link"}</h2>
          <button className="modal-close" onClick={onClose}>
            <FiX />
          </button>
        </div>

        {result ? (
          <div className="modal-result">
            <div className="result-field">
              <label>Short URL</label>
              <div className="result-url">
                <input type="text" value={result.short_url} readOnly />
                <button onClick={copyToClipboard} className="btn-copy" disabled={copied}>
                  {copied ? <><FiCheck size={14} /> Copied</> : "Copy"}
                </button>
              </div>
            </div>
            <div className="result-field">
              <label>Original URL</label>
              <p className="result-original">{result.original_url}</p>
            </div>
            {result.tags && (
              <div className="result-field">
                <label>Tags</label>
                <div className="result-tags">
                  {result.tags.split(",").map((t) => (
                    <span key={t} className="tag-chip">{t}</span>
                  ))}
                </div>
              </div>
            )}
            <button className="btn-primary" onClick={onClose}>
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="modal-form">
            {error && <div className="form-error">{error}</div>}

            <div className="form-group">
              <label htmlFor="url">Destination URL *</label>
              <input
                id="url"
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com/your-long-url"
                required
              />
            </div>

            {/* OG Preview */}
            <div className="form-group">
              <div className="toggle-row">
                <div className="toggle-label-group">
                  <span className="toggle-label">Customize Social Preview</span>
                  <span className="toggle-help">
                    <span className="toggle-help-icon">?</span>
                    <span className="toggle-tooltip">
                      Override the default Open Graph image, title, and description when sharing this link on social media. (Note: Custom previews force a short loading page to prevent abuse).
                    </span>
                  </span>
                </div>
                <label className="toggle-switch">
                  <input type="checkbox" checked={useCustomOg} onChange={(e) => handleToggleCustomOg(e.target.checked)} />
                  <span className="toggle-switch-slider" />
                </label>
              </div>
            </div>

            {useCustomOg ? (
              <div className="custom-og-builder">
                <div className="form-group">
                  <label>Title</label>
                  <input type="text" value={ogTitle} onChange={(e) => setOgTitle(e.target.value)} placeholder="Custom preview title" />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea value={ogDescription} onChange={(e) => setOgDescription(e.target.value)} placeholder="Custom preview description" rows={2} style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: '14px', fontFamily: 'inherit', resize: 'vertical' }} />
                </div>
                <div className="form-group">
                  <label>Image URL</label>
                  <input type="url" value={ogImage} onChange={(e) => setOgImage(e.target.value)} placeholder="https://example.com/image.png" />
                </div>
                {/* Custom OG Preview Card */}
                {(ogTitle || ogImage) && (
                  <div className="og-preview-card">
                    {ogImage && (
                      <img src={ogImage} alt="" className="og-preview-img" onError={(e) => e.target.style.display = 'none'} />
                    )}
                    <div className="og-preview-text">
                      <span className="og-site-name">LinkSquire Redirect</span>
                      <span className="og-title">{ogTitle || "Custom Title"}</span>
                      {ogDescription && (
                        <span className="og-desc">{ogDescription.substring(0, 120)}</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              preview && (preview.title || preview.image) && (
                <div className="og-preview-card">
                  {preview.image && (
                    <img src={preview.image} alt="" className="og-preview-img" onError={(e) => e.target.style.display = 'none'} />
                  )}
                  <div className="og-preview-text">
                    {preview.site_name && <span className="og-site-name">{preview.site_name}</span>}
                    <span className="og-title">{preview.title}</span>
                    {preview.description && (
                      <span className="og-desc">{preview.description.substring(0, 120)}</span>
                    )}
                  </div>
                </div>
              )
            )}

            {/* Instant redirect toggle */}
            <div className="form-group">
              <div className="toggle-row">
                <div className="toggle-label-group">
                  <span className="toggle-label">Instant redirect</span>
                  <span className="toggle-help">
                    <span className="toggle-help-icon">?</span>
                    <span className="toggle-tooltip">
                      Skip the preview landing page and redirect visitors instantly. When off, visitors see a branded preview page with a countdown timer before being redirected.{" "}
                      <a href={`${BASE_DOMAIN}/preview-demo`} target="_blank" rel="noopener noreferrer">See example preview page</a>
                    </span>
                  </span>
                </div>
                <label className="toggle-switch">
                  <input type="checkbox" checked={redirectTime === 0} onChange={(e) => handleToggleInstantRedirect(e.target.checked)} />
                  <span className="toggle-switch-slider" />
                </label>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="title">Title (optional)</label>
              <input
                id="title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="My link name"
                maxLength={255}
              />
            </div>

            <div className="form-group">
              <label htmlFor="campaignId">Campaign (Optional)</label>
              <select
                id="campaignId"
                value={campaignId}
                onChange={(e) => setCampaignId(e.target.value)}
                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
              >
                <option value="">-- No Campaign --</option>
                {campaigns.map(c => (
                  <option key={c.$id} value={c.$id}>{c.name}</option>
                ))}
              </select>
            </div>

            {/* Custom link with base URL prefix */}
            <div className="form-group">
              <label>Short Link</label>
              <div className="code-input-group" style={{ display: 'flex', gap: '8px' }}>
                <select 
                  value={selectedDomain} 
                  onChange={(e) => setSelectedDomain(e.target.value)}
                  style={{ 
                    padding: '8px 12px', 
                    border: '1px solid var(--border)', 
                    borderRadius: 'var(--radius)', 
                    background: 'var(--surface)',
                    fontSize: '14px',
                    color: 'var(--text-secondary)'
                  }}
                >
                  {availableDomains.map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
                <div style={{ flex: 1, position: 'relative', display: 'flex' }}>
                  <span className="code-prefix" style={{ borderRight: 'none', borderTopRightRadius: 0, borderBottomRightRadius: 0 }}>/</span>
                  <input
                    type="text"
                    value={customCode}
                    onChange={(e) => setCustomCode(e.target.value)}
                    placeholder="Leave empty to auto-generate"
                    maxLength={100}
                    style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0, flex: 1 }}
                  />
                </div>
              </div>
              <span className="form-hint">Letters, numbers, hyphens, underscores, and / allowed</span>
            </div>

            {/* UTM Builder */}
            <div className="form-section-toggle" onClick={() => setShowUtm(!showUtm)}>
              <span>UTM Builder {hasUtm && <span className="section-badge">Active</span>}</span>
              {showUtm ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
            </div>
            {showUtm && (
              <div className="utm-builder">
                <div className="form-row">
                  <div className="form-group">
                    <label>Source</label>
                    <input type="text" value={utm.source} onChange={(e) => setUtm({ ...utm, source: e.target.value })} placeholder="eg., google" />
                  </div>
                  <div className="form-group">
                    <label>Medium</label>
                    <input type="text" value={utm.medium} onChange={(e) => setUtm({ ...utm, medium: e.target.value })} placeholder="eg., cpc" />
                  </div>
                </div>
                <div className="form-group">
                  <label>Campaign</label>
                  <input type="text" value={utm.campaign} onChange={(e) => setUtm({ ...utm, campaign: e.target.value })} placeholder="eg., spring_sale" />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Term</label>
                    <input type="text" value={utm.term} onChange={(e) => setUtm({ ...utm, term: e.target.value })} placeholder="eg., keyword" />
                  </div>
                  <div className="form-group">
                    <label>Content</label>
                    <input type="text" value={utm.content} onChange={(e) => setUtm({ ...utm, content: e.target.value })} placeholder="eg., banner_ad" />
                  </div>
                </div>
                {hasUtm && (
                  <div className="utm-preview">
                    <label>Preview URL</label>
                    <code>{buildUrlWithUtm()}</code>
                  </div>
                )}
              </div>
            )}

            {/* Advanced Options */}
            <div className="form-section-toggle" onClick={() => setShowAdvanced(!showAdvanced)}>
              <span>Advanced Options</span>
              {showAdvanced ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
            </div>
            {showAdvanced && (
              <div className="advanced-options">
                <div className="form-group">
                  <label htmlFor="tags">Tags (comma-separated)</label>
                  <input
                    id="tags"
                    type="text"
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    placeholder="eg., marketing, social, campaign"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="mobileUrl">Mobile URL (optional)</label>
                  <input
                    id="mobileUrl"
                    type="url"
                    value={mobileUrl}
                    onChange={(e) => setMobileUrl(e.target.value)}
                    placeholder="https://m.example.com/page"
                  />
                  <span className="form-hint">Mobile users will be redirected here instead</span>
                </div>

                <div className="form-group">
                  <label>Expires at <span className="tz-indicator">({tzAbbrev})</span></label>
                  <div className="datetime-picker">
                    <input
                      type="date"
                      value={expiryDate}
                      onChange={(e) => setExpiryDate(e.target.value)}
                      min={todayStr}
                      className="date-input"
                    />
                    <input
                      type="time"
                      value={expiryTime}
                      onChange={(e) => setExpiryTime(e.target.value)}
                      disabled={!expiryDate}
                      className={`time-input${!expiryDate ? " input-disabled" : ""}`}
                    />
                    {expiryDate && (
                      <button type="button" className="clear-date-btn" onClick={() => { setExpiryDate(""); setExpiryTime("12:00"); }}>
                        <FiX size={12} />
                      </button>
                    )}
                  </div>
                  {expiryDate && (
                    <span className="form-hint">
                      Expires: {formatDateTimeWithTz(localInputToUTC(expiryDate, expiryTime))}
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label>Schedule activation <span className="tz-indicator">({tzAbbrev})</span></label>
                  <div className="datetime-picker">
                    <input
                      type="date"
                      value={scheduleDate}
                      onChange={(e) => setScheduleDate(e.target.value)}
                      min={todayStr}
                      className="date-input"
                    />
                    <input
                      type="time"
                      value={scheduleTime}
                      onChange={(e) => setScheduleTime(e.target.value)}
                      disabled={!scheduleDate}
                      className={`time-input${!scheduleDate ? " input-disabled" : ""}`}
                    />
                    {scheduleDate && (
                      <button type="button" className="clear-date-btn" onClick={() => { setScheduleDate(""); setScheduleTime("00:00"); }}>
                        <FiX size={12} />
                      </button>
                    )}
                  </div>
                  {scheduleDate && (
                    <span className="form-hint">
                      Activates: {formatDateTimeWithTz(localInputToUTC(scheduleDate, scheduleTime))}
                    </span>
                  )}
                  {!scheduleDate && (
                    <span className="form-hint">Link will be active immediately if not scheduled</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="password">Password protection</label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Leave empty for no password"
                  />
                </div>

                <div className={`form-group${redirectTime === 0 ? " timing-slider-disabled" : ""}`}>
                  <label>Preview page timing</label>
                  <div className="timing-slider-group">
                    <div className="timing-slider-track">
                      <input type="range" min={5} max={30} step={5}
                        value={redirectTime === 0 ? 10 : redirectTime}
                        onChange={(e) => setRedirectTime(Number(e.target.value))}
                        disabled={redirectTime === 0} />
                      <div className="timing-slider-ticks">
                        {[5, 10, 15, 20, 25, 30].map((v) => (
                          <span key={v} className={`timing-tick${(redirectTime === 0 ? 10 : redirectTime) === v ? " active" : ""}`}>
                            <span className="timing-tick-line" />
                            <span className="timing-tick-label">{v}s</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <span className="form-hint">How long the preview page is shown before redirecting. Disabled when instant redirect is on.</span>
                </div>

                <div className="form-group">
                  <label>Redirect type</label>
                  <select value={redirectType} onChange={(e) => setRedirectType(e.target.value)} className="form-select">
                    <option value="302">302 — Temporary redirect (default)</option>
                    <option value="301">301 — Permanent redirect</option>
                  </select>
                  <span className="form-hint">301 is cached by browsers. Use 302 unless you need permanent redirects for SEO.</span>
                </div>
              </div>
            )}

            {/* Expiration Fallback — collapsed by default */}
            <div className="form-section-toggle" onClick={() => setShowFallback(!showFallback)}>
              <span>Expiration Fallback</span>
              {fallbackMode && <span className="form-section-badge">Configured</span>}
              {showFallback ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
            </div>
            {showFallback && (
              <div className="fallback-options">
                <p className="form-hint" style={{ marginBottom: 16 }}>
                  Configure what happens when visitors click an expired link.
                  {!expiryDate && <strong> Set an expiration date above to enable.</strong>}
                </p>

                <div className="form-group">
                  <label>When this link expires:</label>
                  <div className="radio-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="fallbackModeCreate"
                        value=""
                        checked={fallbackMode === ""}
                        onChange={() => setFallbackMode("")}
                      />
                      <span>Show default expired page</span>
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="fallbackModeCreate"
                        value="redirect"
                        checked={fallbackMode === "redirect"}
                        onChange={() => setFallbackMode("redirect")}
                      />
                      <span>Redirect to another URL</span>
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="fallbackModeCreate"
                        value="page"
                        checked={fallbackMode === "page"}
                        onChange={() => setFallbackMode("page")}
                      />
                      <span>Show custom expired page</span>
                    </label>
                  </div>
                </div>

                {fallbackMode === "redirect" && (
                  <div className="form-group">
                    <label htmlFor="create-fallback-url">Fallback URL</label>
                    <input
                      id="create-fallback-url"
                      type="url"
                      value={fallbackUrl}
                      onChange={(e) => setFallbackUrl(e.target.value)}
                      placeholder="https://example.com/new-offer"
                      className={!fallbackUrlValid ? "input-error" : ""}
                    />
                    {!fallbackUrlValid && (
                      <span className="form-error">Please enter a valid URL (http:// or https://)</span>
                    )}
                    <span className="form-hint">Visitors will be immediately redirected here when the link expires</span>
                  </div>
                )}

                {fallbackMode === "page" && (
                  <>
                    <div className="form-group">
                      <label htmlFor="create-fallback-message">Custom Message</label>
                      <textarea
                        id="create-fallback-message"
                        value={fallbackMessage}
                        onChange={(e) => setFallbackMessage(e.target.value)}
                        placeholder="This offer has ended. Check out our latest deals!"
                        rows={3}
                        maxLength={500}
                      />
                      <span className="form-hint">This message will be shown to visitors</span>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="create-fallback-cta-text">Button Text</label>
                        <input
                          id="create-fallback-cta-text"
                          type="text"
                          value={fallbackCtaText}
                          onChange={(e) => setFallbackCtaText(e.target.value)}
                          placeholder="See Latest Deals"
                          maxLength={100}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="create-fallback-cta-url">Button URL</label>
                        <input
                          id="create-fallback-cta-url"
                          type="url"
                          value={fallbackCtaUrl}
                          onChange={(e) => setFallbackCtaUrl(e.target.value)}
                          placeholder="https://example.com/deals"
                          className={fallbackCtaUrl && !fallbackCtaUrlValid ? "input-error" : ""}
                        />
                        {fallbackCtaUrl && !fallbackCtaUrlValid && (
                          <span className="form-error">Please enter a valid URL (http:// or https://)</span>
                        )}
                      </div>
                    </div>

                    <div className="form-group checkbox-inline">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={fallbackAutoRedirect}
                          onChange={(e) => setFallbackAutoRedirect(e.target.checked)}
                          disabled={!fallbackCtaUrl}
                        />
                        <span>Auto-redirect after</span>
                      </label>
                      <input
                        type="number"
                        value={fallbackDelay}
                        onChange={(e) => setFallbackDelay(Math.max(1, Math.min(60, parseInt(e.target.value) || 5)))}
                        min={1}
                        max={60}
                        disabled={!fallbackAutoRedirect || !fallbackCtaUrl}
                        className={`delay-input${!fallbackDelayValid ? " input-error" : ""}`}
                      />
                      <span className="delay-label">seconds</span>
                    </div>
                    {!fallbackDelayValid && (
                      <span className="form-error">Delay must be between 1 and 60 seconds</span>
                    )}
                    {!fallbackCtaUrl && fallbackAutoRedirect && (
                      <span className="form-hint" style={{ color: "var(--warning)" }}>Add a Button URL to enable auto-redirect</span>
                    )}
                    {fallbackAutoRedirect && fallbackCtaUrl && (
                      <div className="countdown-preview">
                        <span className="countdown-preview-text">
                          Preview: Redirecting in <strong>{fallbackDelay}</strong>s → {fallbackCtaUrl.length > 40 ? fallbackCtaUrl.slice(0, 40) + "..." : fallbackCtaUrl}
                        </span>
                      </div>
                    )}

                    {/* Custom Branding */}
                    <div className="form-section-toggle" style={{ marginTop: 20, marginBottom: 12 }} onClick={() => {}}>
                      <span style={{ fontSize: 13, fontWeight: 500, color: "var(--text)" }}>Custom Branding (optional)</span>
                      <span className="toggle-help">
                        <span className="toggle-help-icon">?</span>
                        <span className="toggle-tooltip">Replace the LinkSquire logo with your own brand on the expired page.</span>
                      </span>
                    </div>
                    <div className="form-group">
                      <label htmlFor="create-custom-brand-icon">Brand Icon URL</label>
                      <input
                        id="create-custom-brand-icon"
                        type="url"
                        value={customBrandIcon}
                        onChange={(e) => setCustomBrandIcon(e.target.value)}
                        placeholder="https://example.com/logo.png"
                      />
                      <span className="form-hint">Your logo will replace the LinkSquire icon on the expired page</span>
                    </div>
                    <div className="form-group">
                      <label htmlFor="create-custom-brand-name">Brand Name</label>
                      <input
                        id="create-custom-brand-name"
                        type="text"
                        value={customBrandName}
                        onChange={(e) => setCustomBrandName(e.target.value)}
                        placeholder="Your Brand Name"
                        maxLength={100}
                      />
                      <span className="form-hint">Text shown next to your brand icon (or alone if no icon)</span>
                    </div>
                    {(customBrandIcon || customBrandName) && (
                      <div className="form-group">
                        <label style={{ fontSize: 13, fontWeight: 500, color: "var(--text)" }}>Preview</label>
                        <div className="brand-preview-card">
                          {customBrandIcon && <img src={customBrandIcon} alt="Brand" className="brand-preview-icon" onError={(e) => e.target.style.display = 'none'} />}
                          {customBrandName && <span className="brand-preview-name">{customBrandName}</span>}
                          {!customBrandIcon && !customBrandName && <span className="brand-preview-name">LinkSquire</span>}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {fallbackMode && (
                  <button
                    type="button"
                    className="btn-clear-fallback"
                    onClick={() => {
                      setFallbackMode("");
                      setFallbackUrl("");
                      setFallbackMessage("");
                      setFallbackCtaText("");
                      setFallbackCtaUrl("");
                      setFallbackAutoRedirect(false);
                      setFallbackDelay(5);
                      setCustomBrandIcon("");
                      setCustomBrandName("");
                    }}
                  >
                    Clear fallback settings
                  </button>
                )}
              </div>
            )}

            <button type="submit" className="btn-primary" disabled={loading || !fallbackFieldsValid}>
              {loading ? <><span className="spinner-sm" /> Creating...</> : "Create Link"}
            </button>
          </form>
        )}
      </div>

      {/* Toggle conflict confirmation */}
      {showToggleWarning && (
        <div className="modal-overlay" onClick={() => setShowToggleWarning(null)} style={{ zIndex: 1000 }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <h2>Action Required</h2>
              <button className="modal-close" onClick={() => setShowToggleWarning(null)}><FiX /></button>
            </div>
            <p style={{ fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.5, marginBottom: 16 }}>
              {showToggleWarning === 'instant' 
                ? "Turning on instant redirect will disable Custom Social Previews and clear any unsaved custom preview data. Do you want to proceed?"
                : "Turning on Custom Social Previews will disable Instant Redirect, as custom previews require a minimum 5s preview page to prevent abuse. Do you want to proceed?"}
            </p>
            <div className="confirm-actions">
              <button className="btn-confirm-cancel" onClick={() => setShowToggleWarning(null)}>
                Cancel
              </button>
              <button className="btn-confirm-discard" onClick={confirmToggle} style={{ background: "var(--primary)", color: "#fff", borderColor: "var(--primary)" }}>
                Proceed
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
